import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { notification } from "@findemdev/maui";
import { useCallback, useEffect, useState } from "react";
import { useAuthenticateUserMutation, useGetCsrfTokenQuery } from "../services/apis";
import { matchesUrl } from "@findemdev/findem-helpers";


export enum LOGIN_TYPE {
  BASIC_AUTH = 'BASIC_AUTH',
  MICROSOFT_SSO = 'MICROSOFT_SSO'
}
export interface IAuthenticationProps {
  email: string | null;
  password: string | null;
  loginType?: LOGIN_TYPE | undefined
}

const isFetchBaseQueryError = (error: unknown): error is FetchBaseQueryError => {
  return typeof error === "object" && error !== null && "status" in error;
};

const getLoginErrorDescription = (error: unknown): string => {
  if (!isFetchBaseQueryError(error)) {
    return "Please try again.";
  }

  if (typeof error.data === "string") {
    return error.data;
  }

  if (typeof error.data === "object" && error.data !== null && "message" in error.data) {
    return String(error.data.message);
  }

  return "Please try again.";
};

export const useAuthenticateUser = (props:IAuthenticationProps ) => {
  const { email, password, loginType } = props;

  const [isLoggingLoading, setIsLoggingLoading] = useState<boolean>(false);
  const {
    data: csrfTokenObj,
    isLoading: isCsrfLoading
  } = useGetCsrfTokenQuery();
  const [authenticateUserMutation] = useAuthenticateUserMutation();

  const authenticateUser = useCallback(async () => {
    if(!loginType) {
      return;
    }

    if(loginType === LOGIN_TYPE.MICROSOFT_SSO) {
      const host = window.location.hostname.split('.')[0];
      window.location.href = `${matchesUrl}/auth/saml/login?_csrf=${csrfTokenObj?.csrfToken ?? ''}&domain=russellreynolds.com&domain_prefix=${host}`

      return;
    }

    setIsLoggingLoading(true);

    if(!email || !password) {
      return;
    }

    try {
      await authenticateUserMutation({
        email,
        password,
        csrf: csrfTokenObj?.csrfToken ?? ''
      }).unwrap();
      setIsLoggingLoading(false);

      window.location.href = '/my-apps'
    }
    catch(err) {
      notification.error({
        message: 'Login failed',
        description: getLoginErrorDescription(err)
      })
      setIsLoggingLoading(false);
    }
  }, [email, password, csrfTokenObj, loginType, authenticateUserMutation])

  useEffect(() => {
    if(isCsrfLoading) {
      return
    }

    if (!csrfTokenObj || !csrfTokenObj?.csrfToken) {
      return;
    } else {
      const invalidBasicAuth: boolean = ((loginType === LOGIN_TYPE.BASIC_AUTH) && (!email || !password));
      if(!loginType || invalidBasicAuth)
        return;

      authenticateUser();
    }
  }, [csrfTokenObj, authenticateUser, isCsrfLoading, email, password, loginType])

  return {
    isLoggingLoading
  }
}
